import { FileUploadItem } from '@/components/FileList';
import { execute } from '@/graphql/execute';
import { GetAttachmentsQuery } from '@/graphql/graphql';
import { useSubscription } from '@/hooks/useSubscription';
import {
  abortUpload,
  completeUpload,
  completeUploadBatch,
  getAttachments,
  startUpload,
  startUploadBatch,
  uploadPart,
} from '@/queries/attachment.query';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { analytics } from '@/lib/analytics';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const shpExtensions = ['shp', 'shx', 'dbf', 'prj', 'cpg', 'sbn', 'sbx', 'shp.xml', 'dt.dbf', 'ls.xml', 'lt.xml'];

// Extended FileUploadItem interface for upload functionality
export interface UploadFileItem extends FileUploadItem {
  attachmentId?: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'cancelled';
  error?: string;
  uploadedBytes: number;
}

const CHUNK_SIZE = 32 * 1024 * 1024;
const MAX_CONCURRENT_FILES = 4;
const MAX_CONCURRENT_PARTS = 8;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

class Semaphore {
  private queue: (() => void)[] = [];
  private active = 0;

  constructor(private max: number) {}

  async acquire(): Promise<void> {
    if (this.active < this.max) {
      this.active++;
      return;
    }
    return new Promise<void>((resolve) => {
      this.queue.push(resolve);
    });
  }

  release(): void {
    this.active--;
    const next = this.queue.shift();
    if (next) {
      this.active++;
      next();
    }
  }
}

async function checksumSHA256Base64(file: File): Promise<string | null> {
  // chrome does not support files larger than 2GB in arrayBuffer
  if (file.size > 2 * 1024 * 1024 * 1024) return null;
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const byteArray = new Uint8Array(hashBuffer);
  let binary = '';
  for (let i = 0; i < byteArray.length; i++) {
    binary += String.fromCharCode(byteArray[i]);
  }
  return btoa(binary);
}

async function checksumSHA256Base64FromBuffer(buffer: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const byteArray = new Uint8Array(hashBuffer);
  let binary = '';
  for (let i = 0; i < byteArray.length; i++) {
    binary += String.fromCharCode(byteArray[i]);
  }
  return btoa(binary);
}

function guessMimeType(file: File): string {
  // If file.type is available and not empty, use it
  if (file.type && file.type.trim() !== '') {
    return file.type;
  }

  // Extract extension from filename
  const extension = file.name.toLowerCase().split('.').pop();

  // Map of extensions to MIME types
  const extensionMimeMap: Record<string, string> = {
    las: 'application/vnd.las',
    e57: 'application/e57',
    laz: 'application/vnd.laszip',
    geojson: 'application/geo+json',
    kml: 'application/vnd.google-earth.kml+xml',
    kmz: 'application/vnd.google-earth.kmz',
    shp: 'application/x-shapefile',
    gpx: 'application/gpx+xml',
    dxf: 'application/dxf',
    dwg: 'application/acad',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    tar: 'application/x-tar',
    gz: 'application/gzip',
    csv: 'text/csv',
    '7z': 'application/x-7z-compressed',
    ifc: 'application/x-step',
    xml: 'application/xml',
    tif: 'image/tiff',
    tfw: 'text/plain',
    jgw: 'text/plain',
    jpgw: 'text/plain',
    jpw: 'text/plain',
    wld: 'text/plain',
  };

  // Return mapped MIME type or fallback to octet-stream
  return extensionMimeMap[extension || ''] || 'application/octet-stream';
}

const formatSpeed = (bytesPerSecond: number): string => {
  if (bytesPerSecond === 0) return '0 B/s';
  const k = 1024;
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
  return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatRemainingTime = (seconds: number): string => {
  if (!isFinite(seconds) || seconds <= 0) return 'Calculating...';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s remaining`;
  } else {
    return `${secs}s remaining`;
  }
};

// Validate Shapefile completeness
const validateShapefiles = (files: FileUploadItem[]): string[] => {
  const warnings: string[] = [];

  // Group files by their base name (without extension)
  const shapefileGroups = new Map<string, Set<string>>();

  files.forEach((file) => {
    const extension = file.name.toLowerCase().split('.').pop() || '';
    const extension2 = file.name.toLowerCase().split('.').slice(-2).join('.');
    if (shpExtensions.includes(extension) || shpExtensions.includes(extension2)) {
      // const baseName = file.path.replace(/\.[^/.]+$/, '');
      const baseName = shpExtensions.includes(extension2)
        ? file.path.replace(`.${extension2}`, '')
        : file.path.replace(`.${extension}`, '');
      if (!shapefileGroups.has(baseName)) {
        shapefileGroups.set(baseName, new Set());
      }
      shapefileGroups.get(baseName)!.add(extension || '');
    }
  });

  // Check each shapefile group for mandatory files
  shapefileGroups.forEach((extensions, baseName) => {
    const mandatoryFiles = ['shp', 'shx', 'dbf'];
    const missingFiles = mandatoryFiles.filter((ext) => !extensions.has(ext));

    if (missingFiles.length > 0) {
      const fileName = baseName.split('/').pop() || baseName;
      warnings.push(
        `Shapefile "${fileName}" is missing mandatory files: ${missingFiles.map((ext) => `.${ext}`).join(', ')}`,
      );
    }
  });

  return warnings;
};

export function useUpload({ showProgressToast = true }: { showProgressToast?: boolean } = {}) {
  const { projectId: id } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('project');
  const { subscription } = useSubscription();
  const [files, setFiles] = useState<UploadFileItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // True once every file is uploaded and the batch is sealed (processing may
  // start). Cleared again when the post-upload reset runs.
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<GetAttachmentsQuery['attachments']>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const currentBatchId = useRef<string | null>(null);
  const client = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  const uploadStartTimeRef = useRef<number>(0);
  const lastProgressUpdateRef = useRef<{ time: number; bytes: number }>({ time: 0, bytes: 0 });
  const uploadToastIdRef = useRef<string | number | null>(null);
  const currentSpeedRef = useRef<number>(0);
  const partSpeedsRef = useRef<Map<string, number>>(new Map()); // Track speed for each active part upload
  const partSemaphoreRef = useRef<Semaphore>(new Semaphore(MAX_CONCURRENT_PARTS));
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const updateUploadSpeed = (bytesUploaded: number) => {
    const now = Date.now();
    const timeDiff = now - lastProgressUpdateRef.current.time;

    if (timeDiff > 1000) {
      // Update speed every second
      const bytesDiff = bytesUploaded - lastProgressUpdateRef.current.bytes;
      const speed = bytesDiff / (timeDiff / 1000); // bytes per second
      setUploadSpeed(speed);
      lastProgressUpdateRef.current = { time: now, bytes: bytesUploaded };
    }
  };

  const updateToastWithProgress = (progress: number, totalFiles: number) => {
    if (uploadToastIdRef.current) {
      toast.loading(t('upload.toastUploadingTitle'), {
        description: `${Math.round(progress)}% — ${formatSpeed(currentSpeedRef.current)} — ${totalFiles} files`,
        id: uploadToastIdRef.current,
      });
    }
  };

  const uploadPartToS3 = async (
    file: File,
    attachmentId: string,
    partNumber: number,
    totalParts: number,
    retryCount = 0,
    onProgress?: (progress: number) => void,
  ): Promise<{ PartNumber: number; ETag: string; ChecksumSHA256: string }> => {
    const partKey = `${attachmentId}-${partNumber}`;
    const semaphore = partSemaphoreRef.current;

    await semaphore.acquire();
    let released = false;
    const release = () => {
      if (!released) {
        released = true;
        semaphore.release();
      }
    };

    try {
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Upload cancelled');
      }

      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const chunkBuffer = await chunk.arrayBuffer();
      const chunkChecksum = await checksumSHA256Base64FromBuffer(chunkBuffer);

      // Get upload URL for this part
      const partData = await execute(uploadPart, {
        attachmentId,
        partNumber,
        contentLength: chunk.size,
        checksumSHA256Base64: chunkChecksum,
      });

      const uploadUrl = partData?.url;
      if (!uploadUrl) {
        throw new Error(`Failed to get upload URL for part ${partNumber}`);
      }

      // Upload the chunk to S3 with axios and progress tracking
      const uploadResponse = await axios.put(uploadUrl, chunk, {
        headers: {
          'Content-Type': file.type,
          'X-Amz-Checksum-Sha256': chunkChecksum,
        },
        signal: abortControllerRef.current?.signal,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            onProgress(progress);

            // Track this part's upload speed
            const partSpeed = progressEvent.rate || 0;
            partSpeedsRef.current.set(partKey, partSpeed);

            // Calculate total speed by summing all active part speeds
            const totalSpeed = Array.from(partSpeedsRef.current.values()).reduce((sum, speed) => sum + speed, 0);
            currentSpeedRef.current = totalSpeed;
          }
        },
      });

      // Get ETag from response headers
      const etag = uploadResponse.headers.etag;
      if (!etag) {
        throw new Error(`No ETag received for part ${partNumber}`);
      }

      // Clean up this part's speed tracking when upload completes
      partSpeedsRef.current.delete(partKey);
      release();

      return {
        PartNumber: partNumber,
        ETag: etag.replace(/"/g, ''), // Remove quotes from ETag
        ChecksumSHA256: chunkChecksum,
      };
    } catch (error) {
      // Clean up speed tracking on error
      partSpeedsRef.current.delete(partKey);
      release();

      if (error instanceof Error && error.name === 'AbortError') {
        throw error; // Don't retry cancelled uploads
      }

      if (retryCount < MAX_RETRIES) {
        console.warn(`Retrying part ${partNumber} (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
        await sleep(RETRY_DELAY_MS * Math.pow(2, retryCount)); // Exponential backoff
        return uploadPartToS3(file, attachmentId, partNumber, totalParts, retryCount + 1, onProgress);
      }

      throw error;
    }
  };

  const updateOverallProgress = (queue: UploadFileItem[]): number => {
    const totalSize = queue.reduce((sum, item) => sum + item.file.size, 0);
    const uploadedSize = queue.reduce((sum, item) => sum + item.uploadedBytes, 0);

    setTotalBytes(totalSize);
    setUploadedBytes(uploadedSize);

    let progress = 0;
    if (totalSize > 0) {
      progress = (uploadedSize / totalSize) * 100;
      setUploadProgress(progress);
    }

    updateUploadSpeed(uploadedSize);

    // Calculate remaining time based on current speed
    const remainingBytes = totalSize - uploadedSize;
    const speed = currentSpeedRef.current;

    if (speed > 0 && remainingBytes > 0) {
      const timeInSeconds = remainingBytes / speed;
      setRemainingTime(timeInSeconds);
    } else {
      setRemainingTime(0);
    }

    return progress;
  };

  const uploadSingleFile = async (fileItem: UploadFileItem, fileIndex?: number, totalFiles?: number): Promise<void> => {
    if (!id) throw new Error('No project ID');

    try {
      // Update file status to uploading
      setFiles((prev) => {
        const newQueue = prev.map((item) =>
          item.id === fileItem.id ? { ...item, status: 'uploading' as const } : item,
        );
        updateOverallProgress(newQueue);
        return newQueue;
      });

      // Determine the content type
      const contentType = guessMimeType(fileItem.file);

      // Start the upload
      const data = await execute(startUpload, {
        project: id,
        filename: fileItem.path,
        contentType,
        checksumSHA256Base64: await checksumSHA256Base64(fileItem.file),
        batch: currentBatchId.current,
      });

      const attachment = data?.attachment;
      if (!attachment?.id) {
        throw new Error('Failed to start upload - no attachment ID received');
      }

      // Update with attachment ID
      setFiles((prev) => {
        const newQueue = prev.map((item) =>
          item.id === fileItem.id ? { ...item, attachmentId: attachment.id } : item,
        );
        return newQueue;
      });

      // Split file into chunks and upload parts in parallel
      const totalParts = Math.ceil(fileItem.file.size / CHUNK_SIZE);
      const parts: { PartNumber: number; ETag: string; ChecksumSHA256: string }[] = [];
      let completedParts = 0;

      // Track progress for each part individually
      const partProgress: number[] = new Array(totalParts).fill(0);
      const partCompleted: boolean[] = new Array(totalParts).fill(false);

      // Create an array of part numbers to upload
      const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);

      // Process parts in batches with concurrency limit
      const uploadPromises: Promise<void>[] = [];
      let currentIndex = 0;

      const updateFileProgress = () => {
        // Calculate total progress based on completed parts and current part progress
        const totalProgress = partProgress.reduce((sum, progress, index) => {
          if (partCompleted[index]) {
            return sum + 100; // Completed part
          }
          return sum + progress; // In-progress part
        }, 0);

        const overallProgress = totalProgress / totalParts;

        // Calculate bytes uploaded more precisely
        const totalBytesUploaded = partProgress.reduce((sum, progress, index) => {
          const partSize =
            index === totalParts - 1
              ? fileItem.file.size - index * CHUNK_SIZE // Last part might be smaller
              : CHUNK_SIZE;
          return sum + (progress / 100) * partSize;
        }, 0);

        setFiles((prev) => {
          const newQueue = prev.map((item) =>
            item.id === fileItem.id
              ? {
                  ...item,
                  progress: overallProgress,
                  uploadedBytes: Math.min(totalBytesUploaded, fileItem.file.size),
                }
              : item,
          );
          const progress = updateOverallProgress(newQueue);

          // Update toast with current file progress if we have the necessary info
          if (fileIndex !== undefined && totalFiles !== undefined) {
            updateToastWithProgress(progress, totalFiles);
          }

          return newQueue;
        });
      };

      const processNextPart = async (): Promise<void> => {
        while (currentIndex < partNumbers.length) {
          const partNumber = partNumbers[currentIndex++];
          const partIndex = partNumber - 1; // Convert to 0-based index

          if (abortControllerRef.current?.signal.aborted) {
            throw new Error('Upload cancelled');
          }

          try {
            // Create progress callback for this specific part
            const onPartProgress = (progress: number) => {
              partProgress[partIndex] = progress;
              updateFileProgress();
            };

            const result = await uploadPartToS3(
              fileItem.file,
              attachment.id,
              partNumber,
              totalParts,
              0,
              onPartProgress,
            );
            parts.push(result);
            completedParts++;

            // Mark this part as completed
            partCompleted[partIndex] = true;
            partProgress[partIndex] = 100;
            updateFileProgress();
          } catch (error) {
            console.error(`Failed to upload part ${partNumber}:`, error);
            throw error;
          }
        }
      };

      // Start concurrent workers — global semaphore controls actual concurrency
      for (let i = 0; i < Math.min(MAX_CONCURRENT_PARTS, totalParts); i++) {
        uploadPromises.push(processNextPart());
      }

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      // Sort parts by part number before completing
      parts.sort((a, b) => a.PartNumber - b.PartNumber);

      // Complete the upload
      await execute(completeUpload, {
        attachmentId: attachment.id,
        parts,
      });

      // Update file status to completed
      setFiles((prev) => {
        const newQueue = prev.map((item) =>
          item.id === fileItem.id
            ? { ...item, progress: 100, uploadedBytes: fileItem.file.size, status: 'completed' as const }
            : item,
        );
        updateOverallProgress(newQueue);
        return newQueue;
      });
    } catch (error) {
      console.error(`Failed to upload file ${fileItem.path}:`, error);

      // Update file status to failed
      setFiles((prev) =>
        prev.map((item) =>
          item.id === fileItem.id
            ? {
                ...item,
                status: 'failed' as const,
                error: error instanceof Error ? error.message : 'Upload failed',
              }
            : item,
        ),
      );

      throw error;
    }
  };

  const processUploadQueue = async (queue: UploadFileItem[]) => {
    const pendingFiles = queue.filter((item) => item.status === 'pending');
    const fileSemaphore = new Semaphore(MAX_CONCURRENT_FILES);
    let completedFiles = 0;

    const uploadFileWithConcurrency = async (fileItem: UploadFileItem, fileIndex: number) => {
      await fileSemaphore.acquire();
      try {
        if (abortControllerRef.current?.signal.aborted) return;

        setCurrentStep(`Uploading ${fileItem.path}...`);

        await uploadSingleFile(fileItem, fileIndex, pendingFiles.length);
        completedFiles++;
      } catch {
        console.error(`Skipping failed file: ${fileItem.path}`);
      } finally {
        fileSemaphore.release();
      }
    };

    await Promise.all(pendingFiles.map((file, i) => uploadFileWithConcurrency(file, i)));
  };

  const cancelUpload = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCurrentStep('Cancelling uploads...');

      // Dismiss the upload toast
      if (uploadToastIdRef.current) {
        toast.dismiss(uploadToastIdRef.current);
        uploadToastIdRef.current = null;
      }

      // Cancel all uploads and abort on server
      const uploadingItems = files.filter((item) => item.attachmentId && item.status === 'uploading');

      for (const item of uploadingItems) {
        try {
          await execute(abortUpload, { attachmentId: item.attachmentId! });
        } catch (error) {
          console.error(`Failed to abort upload for ${item.path}:`, error);
        }
      }

      // Clear the entire queue and reset all states
      setFiles([]);
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentStep('');
      setUploadedBytes(0);
      setTotalBytes(0);
      setUploadSpeed(0);
      setRemainingTime(0);
      setError(null);
      currentBatchId.current = null;
      partSpeedsRef.current.clear();
      partSemaphoreRef.current = new Semaphore(MAX_CONCURRENT_PARTS);
    }
  };

  const removeFile = (fileId: string) => {
    setFiles((prev) => {
      const newQueue = prev.filter((item) => item.id !== fileId);
      if (!isUploading && newQueue.length === 0) {
        setUploadProgress(0);
        setUploadedBytes(0);
        setTotalBytes(0);
        setUploadSpeed(0);
        setRemainingTime(0);
      }
      return newQueue;
    });
  };

  const startUploads = async (options?: { label?: string }) => {
    if (files.length === 0) return;
    if (!id) throw new Error('No project ID');

    setIsUploading(true);
    setIsUploadComplete(false);
    setError(null);
    setCurrentStep('Preparing uploads...');

    // Keep progress toasts available for flows without inline upload progress.
    const toastId = showProgressToast
      ? toast.loading('Preparing uploads...', { description: 'Creating upload batch' })
      : null;
    uploadToastIdRef.current = toastId;

    abortControllerRef.current = new AbortController();
    uploadStartTimeRef.current = Date.now();
    lastProgressUpdateRef.current = { time: Date.now(), bytes: 0 };
    partSemaphoreRef.current = new Semaphore(MAX_CONCURRENT_PARTS);

    try {
      // Create a new batch for this upload session
      setCurrentStep('Creating upload batch...');
      const batchData = await execute(startUploadBatch, { project: id, numberOfAttachments: files.length, label: options?.label || null });
      const batchId = batchData?.batch?.id;

      console.log(batchData);

      if (!batchId) {
        throw new Error('Failed to create upload batch');
      }

      currentBatchId.current = batchId;
      setCurrentStep('Preparing uploads...');

      // Update toast for upload start
      if (toastId !== null) {
        toast.loading(t('upload.toastUploadingTitle'), {
          description: `0% — ${formatSpeed(0)} — ${files.length} files`,
          id: toastId,
        });
      }

      await processUploadQueue(files);

      setCurrentStep('Completing upload batch...');

      // Complete the batch after all files are uploaded
      await execute(completeUploadBatch, { id: batchId });

      setCurrentStep('All uploads completed!');
      setIsUploadComplete(true);

      analytics.capture('Files Uploaded', { fileCount: files.length });

      // Show success toast
      toast.success(t('upload.uploadCompleteTitle'), {
        description: t('upload.toastUploadCompleteDescription', { count: files.length }),
        id: toastId ?? undefined,
      });
      client.invalidateQueries({ queryKey: ['batches'] });
      if (currentBatchId.current) {
        // open batch
        navigate(`/projects/${id}/datacenter?batch=${currentBatchId.current}`, { replace: true });
      }

      // Refresh attachments list after successful uploads
      await fetchAttachments();

      // Reset after a short delay
      setTimeout(() => {
        setIsUploading(false);
        setIsUploadComplete(false);
        setUploadProgress(0);
        setCurrentStep('');
        setUploadedBytes(0);
        setTotalBytes(0);
        setUploadSpeed(0);
        setRemainingTime(0);
        setFiles([]);
        currentBatchId.current = null;
        uploadToastIdRef.current = null;
        partSpeedsRef.current.clear();
      }, 2000);
    } catch (error) {
      console.error('Upload queue failed:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        setCurrentStep('Uploads cancelled');
        // Update toast for cancellation
        toast.error('Upload cancelled', {
          description: 'Upload was cancelled by user',
          id: toastId ?? undefined,
        });
      } else {
        setError(error instanceof Error ? error.message : 'Upload failed');
        setCurrentStep('Some uploads failed');
        // Update toast for error
        toast.error('Upload failed', {
          description: error instanceof Error ? error.message : 'Unknown error occurred',
          id: toastId ?? undefined,
        });
      }

      // Clear batch ID on error
      currentBatchId.current = null;

      setTimeout(() => {
        setIsUploading(false);
        setCurrentStep('');
        setError(null);
        setRemainingTime(0);
        uploadToastIdRef.current = null;
        partSpeedsRef.current.clear();
      }, 3000);
    }
  };

  const fetchAttachments = useCallback(async () => {
    if (!id) return;

    setIsLoadingAttachments(true);
    try {
      const data = await execute(getAttachments);
      // Filter attachments for current project
      const projectAttachments =
        data?.attachments?.filter((attachment) => attachment.projectId === id && !attachment.deletedAt) || [];
      setAttachments(projectAttachments);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    } finally {
      setIsLoadingAttachments(false);
    }
  }, [id]);

  const addFiles = (newFiles: FileUploadItem[]) => {
    // Validate file sizes
    const oversizedFiles = newFiles.filter((file) => file.size > 100 * 1024 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      const names = oversizedFiles.map((f) => f.name).join(', ');
      toast.error('File size limit exceeded', {
        description: `${oversizedFiles.length} file(s) exceed the 100 GB limit and were skipped: ${names}`,
        duration: 8000,
      });
    }

    const validFiles = newFiles.filter((file) => file.size <= 100 * 1024 * 1024 * 1024);

    // Convert FileUploadItem to UploadFileItem
    const uploadItems: UploadFileItem[] = validFiles.map((file) => ({
      ...file,
      progress: 0,
      status: 'pending' as const,
      uploadedBytes: 0,
    }));

    setFiles((prev) => {
      const updatedFiles = [...prev, ...uploadItems];

      // Validate Shapefiles on the complete file list to ensure each table is checked only once
      const shapefileWarnings = validateShapefiles(updatedFiles);
      if (shapefileWarnings.length > 0) {
        // Only show warnings for newly incomplete shapefiles
        const previousWarnings = validateShapefiles(prev);
        const newWarnings = shapefileWarnings.filter((warning) => !previousWarnings.includes(warning));

        newWarnings.forEach((warning) => {
          toast.warning('Incomplete Shapefile', {
            description: warning,
            duration: 8000,
          });
        });
      }

      return updatedFiles;
    });
  };

  const clearFiles = () => {
    setFiles([]);
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalBytes(0);
    setUploadSpeed(0);
    setRemainingTime(0);
    setError(null);
    currentBatchId.current = null;
    uploadToastIdRef.current = null;
    partSpeedsRef.current.clear();
  };

  useEffect(() => {
    fetchAttachments();
  }, [id, fetchAttachments]);

  // Calculate storage usage and limits
  const storageInfo = useMemo(() => {
    const storageLimitBytes = subscription.storageLimitBytes;
    const pendingUploadBytes = files.reduce((sum, file) => sum + file.file.size, 0);
    // Note: We don't have real-time storage usage here - that comes from the organization query
    // This just checks if pending uploads would exceed the limit
    return {
      storageLimitBytes,
      pendingUploadBytes,
    };
  }, [subscription.storageLimitBytes, files]);

  // Prevent accidental navigation during upload using browser native features
  // This effect runs when isUploading becomes true and cleans up when it becomes false,
  // ensuring navigation is blocked during upload and restored when complete
  useEffect(() => {
    if (!isUploading) return;

    // Prevent window close/refresh/external navigation
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers require returnValue to be set
      e.returnValue = '';
      return '';
    };

    // Prevent in-app navigation (including React Router) during upload
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check for anchor tags (including React Router Link components)
      const link = target.closest('a');

      if (!link) return;

      // For anchor tags, check the href
      if (link.href) {
        // Allow external links, hash links, and javascript links
        if (
          link.href.startsWith('javascript:') ||
          link.href.startsWith('#') ||
          link.href.startsWith('mailto:') ||
          link.href.startsWith('tel:')
        ) {
          return;
        }

        try {
          const linkUrl = new URL(link.href, window.location.origin);
          const currentUrl = new URL(window.location.href);

          // Block if it's a different path on the same origin
          if (linkUrl.origin === currentUrl.origin && linkUrl.pathname !== currentUrl.pathname) {
            // Prevent ALL handlers from running
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const shouldCancel = window.confirm(t('upload.leaveConfirm'));

            if (shouldCancel) {
              // Cancel upload and wait for it to complete
              cancelUpload().then(() => {
                // Allow navigation after cancel completes
                window.location.href = link.href;
              });
            }
          }
        } catch (error) {
          // If URL parsing fails, block it to be safe
          console.warn('Failed to parse link URL:', error);
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    // Add listeners for both capture and bubble phases to catch all navigation attempts
    document.addEventListener('click', handleClick, true); // Capture phase - fires first
    document.addEventListener('click', handleClick, false); // Bubble phase - backup

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('click', handleClick, false);
    };
  }, [isUploading, cancelUpload, t]);

  return {
    // File management
    files,
    setFiles,
    addFiles,
    removeFile,
    clearFiles,

    // Upload control
    startUploads,
    cancelUpload,
    isUploading,
    isUploadComplete,

    // Batch management
    currentBatchId: currentBatchId.current,

    // Progress tracking
    uploadProgress,
    currentStep,
    uploadedBytes,
    totalBytes,
    uploadSpeed,
    remainingTime,

    // Error handling
    error,
    setError,

    // Attachments
    attachments,
    isLoadingAttachments,
    fetchAttachments,

    // Storage limits
    storageInfo,
  };
}

// Export helper functions for formatting
export { formatRemainingTime, formatSpeed };
